import "./index.css";
import { getLocations } from "./utils/locations.util";
import { AutoComplete } from "./searchBar/autocomplete";
import { docReady } from "./utils/dom.util";
import { FetchParameters } from "./types/api.types";

const SELECTOR_HEADER = "#menu1";
const SELECTOR_FORM = "xchange-public-leasing-search-bar-form";
const PREFILL_DATA = ["pickup", "action"];

class SearchBar {
  public el: HTMLDivElement;
  public form: HTMLFormElement;
  public autocompletePickup!: AutoComplete;
  public autocompleteDropoff!: AutoComplete;

  constructor(el: HTMLDivElement) {
    this.el = el;
    this.form = this.el.querySelector(`#${SELECTOR_FORM}`) as HTMLFormElement;

    this.form.addEventListener("submit", this.handleSubmit.bind(this));

    this.init();
  }

  async init() {
    await this.initAutoComplete();
    this.prefillSearchBar();
  }

  async initAutoComplete() {
    const inputPickup = this.el.querySelector(
      "#autocomplete-pickup"
    ) as HTMLDivElement;

    const inputDropoff = this.el.querySelector(
      "#autocomplete-dropoff"
    ) as HTMLDivElement;

    this.autocompletePickup = new AutoComplete(inputPickup, {
      src: await getLocations(),
      keys: ["searchLabel", "searchLabelAlt"],
    });
    this.autocompleteDropoff = new AutoComplete(inputDropoff, {
      src: await getLocations(),
      keys: ["searchLabel", "searchLabelAlt"],
    });
  }

  handleSubmit(event: Event) {
    event.preventDefault();

    const formValues = new FormData(this.form);
    const urlParams = new URLSearchParams();

    urlParams.append("direction", formValues.get("direction") as string);

    if (formValues.get("pickup-unlo")) {
      urlParams.append(
        "pickUpLocationUnlocode",
        formValues.get("pickup-unlo") as string
      );
    }

    if (formValues.get("dropoff-unlo")) {
      urlParams.append(
        "dropOffLocationUnlocode",
        formValues.get("dropoff-unlo") as string
      );
    }

    const scrollParam = this.el.dataset.scroll;

    if (scrollParam !== null) {
      urlParams.append("scroll", "true");
    }

    const resultPageUrl =
      this.el.dataset.resultPage ||
      window.location.origin + window.location.pathname;

    window.location.href = `${resultPageUrl}?${urlParams.toString()}`;
  }

  prefillSearchBar() {
    const urlParamsFetched = this.fetchUrlParams();

    if (urlParamsFetched) {
      // Return early if url params are set to prevent unwanted overwrite of values
      return;
    }

    PREFILL_DATA.forEach((key) => {
      const value = this.el.getAttribute(`data-prefill-${key}`);

      if (key === "pickup" && value) {
        const allowOverwrite = this.autocompletePickup.getSelectedLocations();

        if (!allowOverwrite) {
          return;
        }

        this.autocompletePickup.addChipByUnlocode(value);
      }

      if (key === "action" && value) {
        this.setDirection(value as "use" | "supply");
      }
    });
  }

  setDirection(direction: "use" | "supply") {
    const parsedDirection = direction === "use" ? "Using" : "Supplying";

    const directionEl = this.el.querySelector(
      `[name="direction"][value="${parsedDirection}"]`
    ) as HTMLInputElement;

    if (directionEl) {
      directionEl.checked = true;
    }
  }

  /**
   * Fetch url params for prefilling the search bar
   */
  fetchUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);

    const direction = urlParams.get("direction");
    const pickup = urlParams.get("pickUpLocationUnlocode");
    const dropoff = urlParams.get("dropOffLocationUnlocode");
    const scroll = urlParams.get("scroll");

    if (pickup) {
      this.autocompletePickup.addChipByUnlocode(pickup);
    }

    if (dropoff) {
      this.autocompleteDropoff.addChipByUnlocode(dropoff);
    }

    if (scroll) {
      setTimeout(() => {
        const headerHeight = Math.max(
          document.querySelector(SELECTOR_HEADER)?.clientHeight || 0,
          70
        );

        const scrollPosition =
          this.el.getBoundingClientRect().top +
          window.scrollY -
          headerHeight -
          32;

        window.scrollTo({ top: scrollPosition, behavior: "smooth" });
      }, 500);
    }

    if (direction || pickup || dropoff) {
      return true;
    }

    return false;
  }
}

docReady(() => {
  const el = document.querySelectorAll(
    ".xchange-public-leasing-search-bar"
  ) as NodeListOf<HTMLDivElement>;

  if (el) {
    el.forEach((el) => new SearchBar(el));
  }
});
